import React, { useEffect, useRef, useState } from 'react';
import './publisher.css';
import authFetch from '../../../utils/authFetch';

const Onboarding = () => {
  const [loading, setLoading] = useState(true);
  const [publisher, setPublisher] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [studies, setStudies] = useState([]);
  const [cvPath, setCvPath] = useState(null);
  const [certificates, setCertificates] = useState([]); // combined: certificates & degrees
  const [coverLetter, setCoverLetter] = useState('');
  const [videoPath, setVideoPath] = useState(null); // legacy single intro
  const [videoAnswers, setVideoAnswers] = useState([]); // [{question, path}]
  const QUESTION_POOL = [
    'Tell us about yourself.',
    'Why do you want to become a publisher?',
    'What topics do you like writing about?',
    'What is your area of expertise?',
    'Describe a challenge you solved through writing or research.',
    'Who is your target audience and why?',
    'Share an example of previous published work (if any).',
    'How do you ensure accuracy in your work?',
    'What motivates your writing?',
    'How do you handle critical feedback?',
    'What is your workflow from idea to publication?',
    'What tools do you use for authoring and formatting?',
    'How do you choose topics?',
    'How do you validate sources?',
    'How do you keep your content accessible?',
    'Describe your editing process.',
    'How will you promote your publications?',
    'What makes your content unique?',
    'What is your most relevant credential and why?',
    'What long-term topics do you plan to cover?'
  ];
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const mediaRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState(null);
  const [step, setStep] = useState(1);
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch('/api/store/publisher/me');
        const json = await res.json();
        if (json?.publisher) {
          setPublisher(json.publisher);
          setDisplayName(json.publisher.display_name || '');
          const profile = json.publisher.profile || {};
          setBio(profile.bio || '');
          setCvPath(profile.cv_path || profile.cv || profile.resume_path || null);
          setCertificates(Array.isArray(profile.certificates) ? profile.certificates : []);
          setCoverLetter(profile.cover_letter || '');
          setVideoPath(profile.video_intro_path || null);
          if (Array.isArray(profile.video_answers)) setVideoAnswers(profile.video_answers);
          if (Array.isArray(profile.video_questions)) setSelectedQuestions(profile.video_questions);
          // CV from profile.cvs fallback
          if (!cvPath && Array.isArray(profile.cvs) && profile.cvs.length>0) {
            const first = profile.cvs[0];
            setCvPath(first?.path || first?.url || null);
          }
        }
        // Prefill from user profile if available
        try {
          const pRes = await authFetch('/api/profile/me');
          if (pRes.ok) {
            const pj = await pRes.json();
            const p = pj?.profile || {};
            if (!displayName && p.display_name) setDisplayName(p.display_name);
            if (!bio && p.bio) setBio(p.bio);
            if (Array.isArray(p.studies) && studies.length === 0) setStudies(p.studies);
            if (!cvPath && (p.cv_path || p.cv || p.resume_path)) setCvPath(p.cv_path || p.cv || p.resume_path);
            if (Array.isArray(p.certificates) && certificates.length === 0) setCertificates(p.certificates);
            if (!cvPath && Array.isArray(p.cvs) && p.cvs.length>0) {
              const first = p.cvs[0];
              setCvPath(first?.path || first?.url || null);
            }
          }
        } catch {}
      } catch (e) {
        setError('Failed to load publisher profile');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const uploadViaSignedUrl = async (fileOrBlob, suggestedName) => {
    const ext = (suggestedName && suggestedName.split('.').pop()) || 'bin';
    const path = `publisher/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const sign = await authFetch('/api/store/publisher/uploads/sign', { method: 'POST', body: { path } });
    const sj = await sign.json();
    if (!sign.ok || !sj?.url) throw new Error('Failed to get signed URL');
    const up = await fetch(sj.url, { method:'PUT', headers: { 'Content-Type': fileOrBlob.type || 'application/octet-stream' }, body: fileOrBlob });
    if (!up.ok) throw new Error('Upload failed');
    return sj.path;
  };

  const onCvSelected = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const p = await uploadViaSignedUrl(f, f.name); setCvPath(p); } catch (err) { setError(err.message); }
  };
  const onCertSelected = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const p = await uploadViaSignedUrl(f, f.name); setCertificates(prev=>[...prev,{ name:f.name, path:p }]); } catch (err) { setError(err.message); }
  };
  // Degrees are combined into certificates field; no separate handler
  const startRecording = async (qIndex = null) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio:true, video:true });
      const mr = new MediaRecorder(stream, { mimeType:'video/webm' });
      mediaRef.current = { mr, chunks:[], stream, qIndex };
      mr.ondataavailable = ev => { if (ev.data?.size) mediaRef.current.chunks.push(ev.data); };
      mr.onstop = async () => {
        const blob = new Blob(mediaRef.current.chunks, { type:'video/webm' });
        try {
          const p = await uploadViaSignedUrl(blob, 'intro.webm');
          if (mediaRef.current.qIndex === null || mediaRef.current.qIndex === undefined) {
            setVideoPath(p);
          } else {
            const q = selectedQuestions[mediaRef.current.qIndex];
            setVideoAnswers(prev => {
              const next = [...prev];
              next[mediaRef.current.qIndex] = { question: q, path: p };
              return next;
            });
          }
        } catch (e) { setError(e.message); }
        mediaRef.current.stream.getTracks().forEach(t=>t.stop());
        setRecording(false);
      };
      mr.start(500); setRecording(true);
    } catch (e) { setError(e.message || 'Cannot record'); }
  };
  const stopRecording = () => { if (mediaRef.current) mediaRef.current.mr.stop(); };

  // Studies editor helpers
  const addStudy = () => setStudies(prev => [...prev, { institution: '', degree: '', year: '' }]);
  const updStudy = (i, patch) => setStudies(prev => prev.map((s, idx) => idx===i ? { ...s, ...patch } : s));
  const delStudy = (i) => setStudies(prev => prev.filter((_, idx) => idx!==i));

  const isComplete = Boolean(
    (displayName || '').trim() &&
    (bio || '').trim() &&
    cvPath &&
    Array.isArray(certificates) && certificates.length > 0 &&
    Array.isArray(studies) && studies.length > 0 &&
    (coverLetter || '').trim().length > 0
  );

  const apply = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!isComplete) {
        setValidation('Please complete all steps: Profile, CV, at least one Certificate, at least one Study, and a Cover Letter.');
        setSaving(false);
        return;
      }
      // First, sync profile so onboarding edits persist to user profile
      const cvs = cvPath ? [{ path: cvPath }] : [];
      const profPatch = { display_name: displayName, bio, studies, certificates, cvs, cv_path: cvPath };
      const pr = await authFetch('/api/profile/me', { method: 'PUT', body: profPatch });
      if (!pr.ok) {
        const pj = await pr.json().catch(()=>({}));
        throw new Error(pj?.message || 'Failed to update profile');
      }
      const profile = { bio, studies };
      const body = { profile, display_name: displayName, cv_path: cvPath, certificates, cover_letter: coverLetter, video_intro_path: videoPath, video_questions: selectedQuestions, video_answers: videoAnswers };
      const res = await authFetch('/api/store/publisher/apply', { method: 'POST', body });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Apply failed');
      // Refresh me
      const me = await authFetch('/api/store/publisher/me').then(r=>r.json());
      setPublisher(me.publisher || null);
      alert('Application submitted. Status: ' + (me.publisher?.status || 'pending'));
    } catch (e) {
      setError(e.message || 'Apply failed');
    } finally {
      setSaving(false);
    }
  };
  const status = publisher?.status || 'not-applied';
  if (loading) return <div className="publisher glass"><div className="loading">Loading...</div></div>;
  return (
    <div className="publisher">
      <div className="header"><h1>Publisher Onboarding</h1>{status!=='not-applied' && <div className={`status badge ${status}`}>Status: {status}</div>}</div>
      <div className="glass section" style={{marginBottom:12}}>
        <div className="actions">
          <button className="btn-neon" onClick={()=>setStep(1)} disabled={step===1}>1. Profile</button>
          <button className="btn-neon" onClick={()=>setStep(2)} disabled={step===2}>2. CV</button>
          <button className="btn-neon" onClick={()=>setStep(3)} disabled={step===3}>3. Certificates & Degrees</button>
          <button className="btn-neon" onClick={()=>setStep(4)} disabled={step===4}>4. Cover Letter</button>
          <button className="btn-neon" onClick={()=>setStep(5)} disabled={step===5}>5. Video Q&A</button>
        </div>
      </div>
      {step===1 && (
        <div className="glass section">
          <h3>Review Profile</h3>
          <div className="form-grid">
            <label>Display name</label>
            <input value={displayName} onChange={(e)=>setDisplayName(e.target.value)} />
            <label>Bio</label>
            <textarea value={bio} onChange={(e)=>setBio(e.target.value)} />
          </div>
          <div className="hint" style={{marginTop:8}}>Studies</div>
          <div className="table">
            <div className="row head"><span>Institution</span><span>Degree</span><span>Year</span><span></span></div>
            {studies.map((s, i) => (
              <div className="row" key={i} style={{gridTemplateColumns:'2fr 2fr 1fr auto'}}>
                <span><input value={s.institution} placeholder="Institution" onChange={e=>updStudy(i,{institution:e.target.value})} /></span>
                <span><input value={s.degree} placeholder="Degree" onChange={e=>updStudy(i,{degree:e.target.value})} /></span>
                <span><input value={s.year} placeholder="Year" onChange={e=>updStudy(i,{year:e.target.value})} /></span>
                <span><button onClick={()=>delStudy(i)}>Remove</button></span>
              </div>
            ))}
            <div className="row"><button className="btn-neon" onClick={addStudy}>Add study</button></div>
          </div>
        </div>
      )}
      {step===2 && (
        <div className="glass section">
          <h3>Upload CV</h3>
          <input type="file" accept=".pdf,.doc,.docx" onChange={onCvSelected} />
          {cvPath && <div className="hint">Uploaded: {cvPath}</div>}
        </div>
      )}
      {step===3 && (
        <div className="glass section">
          <h3>Certificates & Degrees</h3>
          <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={onCertSelected} />
          <ul>{certificates.map((c,i)=>(<li key={i}>{c.name || c.path}</li>))}</ul>
        </div>
      )}
      {step===4 && (
        <div className="glass section">
          <h3>Cover Letter</h3>
          <textarea value={coverLetter} onChange={(e)=>setCoverLetter(e.target.value)} rows={8} />
        </div>
      )}
      {step===5 && (
        <div className="glass section">
          <h3>Video Q&A</h3>
          <div className="hint">Please record one short video answering the questions below. You may pause between answers. Aim for 2-4 minutes total.</div>
          {selectedQuestions.length === 0 && (
            <div style={{margin:'8px 0'}}>
              <button className="btn-neon" onClick={()=>{
                const shuffled = [...QUESTION_POOL].sort(()=>Math.random()-0.5);
                setSelectedQuestions(shuffled.slice(0,3));
                setVideoAnswers([]);
              }}>Generate Questions</button>
            </div>
          )}
          {selectedQuestions.length > 0 && (
            <div>
              <ol>
                {selectedQuestions.map((q, i)=>(
                  <li key={i}>
                    <div style={{display:'flex', gap:8, alignItems:'center'}}>
                      <span style={{flex:1}}>{q}</span>
                      {!recording && <button className="btn-neon" onClick={()=>startRecording(i)}>Record</button>}
                      {recording && mediaRef.current?.qIndex===i && <button className="btn-neon" onClick={stopRecording}>Stop</button>}
                    </div>
                    {videoAnswers[i]?.path && <div className="hint">Uploaded: {videoAnswers[i].path}</div>}
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="hint">You can also record one single intro if preferred:</div>
          {!recording && <button className="btn-neon" onClick={()=>startRecording(null)}>Start Intro Recording</button>}
          {recording && mediaRef.current?.qIndex==null && <button className="btn-neon" onClick={stopRecording}>Stop</button>}
          {videoPath && <div className="hint">Intro Uploaded: {videoPath}</div>}
        </div>
      )}
      {error && <div className="error">{error}</div>}
      {!error && validation && <div className="error">{validation}</div>}
      <div className="actions" style={{marginTop:12}}>
        <button className="btn-neon" onClick={apply} disabled={saving || !isComplete}>{saving?'Saving...':(status==='not-applied'?'Apply':'Update')}</button>
        {status!=='not-applied' && <a className="btn-cta" href="/app/eBookstore/publisher/dashboard">Go to Dashboard →</a>}
      </div>
    </div>
  );
};

export default Onboarding;
